import {restRequest} from '@nlabs/metropolisjs/utils';

import type {FluxFramework} from '@nlabs/arkhamjs';

export type ManagedRuntimeDescriptor = Readonly<{
  appId: string;
  canonicalUrl: string;
  domain?: string;
  kind: 'frontend' | 'full-stack';
  name: string;
}>;
export type ManagedRuntimeState = Readonly<{status: 'loading'} | {status: 'unavailable'} | {descriptor: ManagedRuntimeDescriptor; status: 'ready'}>;
export const MANAGED_RUNTIME_EVENT = 'MANAGED_RUNTIME_CHANGED';
export const INITIAL_RUNTIME: ManagedRuntimeState = {status: 'loading'};
const requests = new WeakMap<FluxFramework, {hostname: string; promise: Promise<void>}>();

const validHostname = (value: string): boolean => value.length <= 253 && value.split('.').length >= 2 &&
  /[a-z]/u.test(value.split('.').at(-1) || '') &&
  value.split('.').every((label) => label.length <= 63 && /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u.test(label));

/** Exact public Task 3 contract; no storage or infrastructure fields are accepted. */
export const decodeManagedRuntime = (value: unknown, hostname: string): ManagedRuntimeDescriptor => {
  if(!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid runtime.');
  }
  const data = value as Record<string, unknown>;
  const {appId, canonicalUrl, domain, kind, name} = data;
  if(Object.keys(data).some((key) => !['appId', 'canonicalUrl', 'domain', 'kind', 'name'].includes(key)) ||
    typeof appId !== 'string' || appId.length > 63 || !/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/u.test(appId) ||
    canonicalUrl !== `https://${appId}.apps.reaktor.io` ||
    (kind !== 'frontend' && kind !== 'full-stack') || typeof name !== 'string' || !name.trim() || name !== name.trim() ||
    ('domain' in data && (typeof domain !== 'string' || !validHostname(domain) || domain === 'reaktor.io' || domain.endsWith('.reaktor.io'))) ||
    !validHostname(hostname) || (`https://${hostname}` !== canonicalUrl && hostname !== domain)) {
    throw new Error('Invalid runtime.');
  }
  return Object.freeze({appId, canonicalUrl, ...(typeof domain === 'string' ? {domain} : {}), kind, name});
};

const publish = async (flux: FluxFramework, state: ManagedRuntimeState): Promise<void> => {
  await flux.setState('managed.runtime', state);
  await flux.dispatch({type: MANAGED_RUNTIME_EVENT});
};

/** Bootstrap is public and intentionally independent of a Metropolis React provider. */
export const loadManagedRuntime = (hostname: string, flux: FluxFramework): Promise<void> => {
  const current = requests.get(flux);
  if(current?.hostname === hostname) {
    return current.promise;
  }
  const request = {hostname, promise: Promise.resolve()};
  requests.set(flux, request);
  request.promise = (async () => {
    await publish(flux, INITIAL_RUNTIME);
    try {
      if(!validHostname(hostname)) {
        throw new Error('Invalid hostname.');
      }
      const response = await restRequest<unknown>(flux, 'https://api.reaktor.io/apps/runtime', 'GET', {host: hostname},
        {authenticate: false, cache: false, credentials: 'omit', queueOffline: false, timeout: 15000});
      const descriptor = decodeManagedRuntime(response, hostname);
      if(requests.get(flux) === request) {
        await publish(flux, {descriptor, status: 'ready'});
      }
    } catch{
      if(requests.get(flux) === request) {
        await publish(flux, {status: 'unavailable'});
      }
    } finally {
      if(requests.get(flux) === request) {
        requests.delete(flux);
      }
    }
  })();
  return request.promise;
};
